import React, { useEffect, useRef, useState } from 'react';
import { brandContent } from '../data/mock';

const ValueCard = ({ title, description, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    const desktopOffset = 80 + index * 80;
    const rootMargin = isMobile ? '0px 0px -40px 0px' : `0px 0px -${desktopOffset}px 0px`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`group relative flex flex-col h-full p-6 sm:p-7 bg-[#FAFAF8] border border-[#D4AF37]/30 transition-all duration-[800ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1.5 hover:shadow-[0_20px_48px_-12px_rgba(212,175,55,0.18),0_4px_16px_-4px_rgba(11,45,130,0.10)] ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {/* Top gold accent line — widens on hover */}
      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent transition-all duration-500 group-hover:left-3 group-hover:right-3 group-hover:via-[#D4AF37]" />

      {/* Corner ornaments */}
      <div className="absolute top-2 left-2 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/40 transition-all duration-500 group-hover:border-[#D4AF37]/80 group-hover:w-3.5 group-hover:h-3.5" />
      <div className="absolute top-2 right-2 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/40 transition-all duration-500 group-hover:border-[#D4AF37]/80 group-hover:w-3.5 group-hover:h-3.5" />
      <div className="absolute bottom-2 left-2 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/40 transition-all duration-500 group-hover:border-[#D4AF37]/80 group-hover:w-3.5 group-hover:h-3.5" />
      <div className="absolute bottom-2 right-2 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/40 transition-all duration-500 group-hover:border-[#D4AF37]/80 group-hover:w-3.5 group-hover:h-3.5" />

      {/* Title */}
      <h3 className="font-heading text-base sm:text-lg font-bold text-primary-royal-blue mb-1.5 text-center">
        {title}
      </h3>

      {/* Gold rule */}
      <div className="h-px mb-6 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent transition-all duration-500 w-8 group-hover:w-14" />

      {/* Description */}
      <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-royal-blue/65 text-center">
        {description}
      </p>

      {/* Bottom gold accent line */}
      <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent transition-all duration-500 group-hover:left-3 group-hover:right-3 group-hover:via-[#D4AF37]/70" />
    </div>
  );
};

const ZurianStandard = () => {
  const gridRef = useRef(null);

  return (
    <section className="relative pt-16 md:pt-20 pb-16 sm:pb-20 md:pb-24 lg:pb-32 paper-texture bg-white">
      {/* Blend seamlessly from the EmailCapture section's beige background */}
      <div
        className="absolute top-0 left-0 right-0 h-[160px] pointer-events-none z-[0]"
        style={{ background: 'linear-gradient(to bottom, #eadec8 0%, transparent 100%)' }}
      />

      <div className="relative z-10 w-full mb-10 sm:mb-12 md:mb-16">
        <div className="w-full flex items-center justify-between gap-4 sm:gap-6">
          <div className="h-px bg-primary-sky-blue flex-grow" />
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-center px-4 shrink-0 text-primary-royal-blue">
            THE ZURIAN WAY
          </h2>
          <div className="h-px bg-primary-sky-blue flex-grow" />
        </div>
        <p className="text-center font-body text-xs sm:text-sm md:text-base mt-4 sm:mt-6 text-primary-royal-blue/70">
          The principles that define our community, our craft and separate us from the rest.
        </p>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        <div
          ref={gridRef}
          className="grid grid-cols-2 lg:grid-cols-5 gap-[18px] sm:gap-[22px]"
        >
          {brandContent.values.map((value, index) => {
            const isOrphan = index === brandContent.values.length - 1 && brandContent.values.length % 2 === 1;
            return (
              <div key={value.id} className={isOrphan ? 'col-span-2 lg:col-span-1 h-full' : 'h-full'}>
                <div className={isOrphan ? 'w-[calc(50%-9px)] sm:w-[calc(50%-11px)] lg:w-full mx-auto h-full' : 'w-full h-full'}>
                  <ValueCard
                    title={value.title}
                    description={value.description}
                    index={index}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ZurianStandard;
